import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Info } from "lucide-react";

/** Mandantenkonfiguration: Land, Währung, Steuer-Vorgaben, Sprachen.
 *  Autorisierung erfolgt serverseitig (nur Admin des eigenen Mandanten darf schreiben). */
export default function TenantConfigTab({ isAdmin, onSaved }) {
  const { t, reloadConfig } = useI18n();
  const [reg, setReg] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/tenant-config/registry").then((r) => setReg(r.data)).catch(() => {});
    api.get("/tenant-config").then((r) => setCfg(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg || !reg) return <div className="text-muted-foreground">{t("common.loading")}</div>;

  const euCountry = !!reg.countries.find((c) => c.country_code === cfg.country_code)?.eu_member;

  const set = (patch) => setCfg({ ...cfg, ...patch });

  const save = async (extra = {}, only = null) => {
    setBusy(true);
    try {
      const full = {
        country_code: cfg.country_code, base_currency: cfg.base_currency,
        default_language: cfg.default_language, enabled_languages: cfg.enabled_languages,
        tax_standard_rate: cfg.tax_standard_rate, tax_reduced_rate: cfg.tax_reduced_rate,
        prices_include_tax: cfg.prices_include_tax, tax_calculation_mode: cfg.tax_calculation_mode,
        tax_number: cfg.tax_number, tax_rates: cfg.tax_rates, paper_format: cfg.paper_format,
        expiry_warning_days: cfg.expiry_warning_days, expiry_critical_days: cfg.expiry_critical_days,
      };
      const body = { ...(only || full), ...extra };
      const r = await api.put("/tenant-config", body);
      setCfg(r.data);
      await reloadConfig();
      if (onSaved) onSaved();
      toast.success(t("common.saved"));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleLang = (loc) => {
    const cur = cfg.enabled_languages || [];
    const next = cur.includes(loc) ? cur.filter((x) => x !== loc) : [...cur, loc].sort();
    set({ enabled_languages: next });
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="tenant-config-tab">
      <div>
        <div className="font-heading text-lg font-semibold">{t("tc.title")}</div>
        <p className="text-sm text-muted-foreground">{t("tc.subtitle")}</p>
      </div>

      {cfg.config_status === "needs_configuration" && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm" data-testid="tc-needs-config">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive shrink-0" />
          <span>{t("tc.needsConfiguration")}</span>
        </div>
      )}
      {!isAdmin && (
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm" data-testid="tc-readonly-hint">{t("tc.readOnly")}</div>
      )}

      <div className="rounded-md border border-border bg-card p-5 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>{t("common.country")}</Label>
            <Select value={cfg.country_code || ""} disabled={!isAdmin}
              onValueChange={(v) => set({ country_code: v })}>
              <SelectTrigger className="mt-1.5" data-testid="tc-country"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {reg.countries.map((c) => (
                  <SelectItem key={c.country_code} value={c.country_code} data-testid={`tc-country-${c.country_code}`}>
                    {c.name_de} ({c.country_code})
                  </SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("tc.baseCurrency")}</Label>
            <Select value={cfg.base_currency || "EUR"} disabled={!isAdmin}
              onValueChange={(v) => set({ base_currency: v })}>
              <SelectTrigger className="mt-1.5" data-testid="tc-currency"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {reg.currencies.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`tc-currency-${c.code}`}>{c.code} · {c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("tc.standardRate")}</Label>
            <Input type="number" step="0.1" className="mt-1.5" disabled={!isAdmin} data-testid="tc-standard-rate"
              value={cfg.tax_standard_rate ?? ""} onChange={(e) => set({ tax_standard_rate: e.target.value === "" ? null : Number(e.target.value) })} />
          </div>
          <div>
            <Label>{t("tc.reducedRate")}</Label>
            <Input type="number" step="0.1" className="mt-1.5" disabled={!isAdmin} data-testid="tc-reduced-rate"
              value={cfg.tax_reduced_rate ?? ""} onChange={(e) => set({ tax_reduced_rate: e.target.value === "" ? null : Number(e.target.value) })} />
          </div>
          <div>
            <Label>{t("tc.calcMode")}</Label>
            <Select value={cfg.tax_calculation_mode || "line_item"} disabled={!isAdmin}
              onValueChange={(v) => set({ tax_calculation_mode: v })}>
              <SelectTrigger className="mt-1.5" data-testid="tc-calc-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {reg.tax_calculation_modes.map((m) => (
                  <SelectItem key={m} value={m} data-testid={`tc-calc-mode-${m}`}>{t(`tc.calcMode.${m}`)}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("set.paperFormat")}</Label>
            <Select value={cfg.paper_format || "A4"} disabled={!isAdmin}
              onValueChange={(v) => set({ paper_format: v })}>
              <SelectTrigger className="mt-1.5" data-testid="tc-paper-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="A4" data-testid="tc-paper-format-A4">DIN A4 (210 × 297 mm)</SelectItem>
                <SelectItem value="LETTER" data-testid="tc-paper-format-LETTER">US Letter (8,5 × 11 in)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("tc.taxNumber")}</Label>
            <Input className="mt-1.5" disabled={!isAdmin} data-testid="tc-tax-number"
              value={cfg.tax_number || ""} onChange={(e) => set({ tax_number: e.target.value })} />
          </div>
        </div>

        {cfg.paper_format === "LETTER" && euCountry && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm" data-testid="tc-paper-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span>
              <strong>{t("set.paperWarnLabel")}</strong> {t("set.paperWarn")}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border pt-4">
          <Label>{t("tc.pricesIncludeTax")}</Label>
          <Switch checked={!!cfg.prices_include_tax} disabled={!isAdmin} data-testid="tc-prices-include-tax"
            onCheckedChange={(v) => set({ prices_include_tax: v })} />
        </div>

        <div className="border-t border-border pt-4">
          <Label>{t("tc.defaultLanguage")}</Label>
          <Select value={cfg.default_language || "de"} disabled={!isAdmin}
            onValueChange={(v) => set({ default_language: v, enabled_languages: [...new Set([...(cfg.enabled_languages || []), v])].sort() })}>
            <SelectTrigger className="mt-1.5 max-w-xs" data-testid="tc-default-language"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {reg.languages.map((l) => (
                <SelectItem key={l.locale} value={l.locale} data-testid={`tc-default-language-${l.locale}`}>{l.endonym} ({l.locale})</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>{t("tc.enabledLanguages")}</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="tc-enabled-languages">
            {reg.languages.map((l) => {
              const on = (cfg.enabled_languages || []).includes(l.locale);
              return (
                <button key={l.locale} type="button" disabled={!isAdmin} onClick={() => toggleLang(l.locale)}
                  data-testid={`tc-lang-${l.locale}`}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    on ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-muted"}`}>
                  {l.endonym}
                </button>);
            })}
          </div>
        </div>

        <div className="border-t border-border pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>{t("ex.warningDays")}</Label>
              <Input type="number" min="1" className="mt-1.5" disabled={!isAdmin} data-testid="tc-expiry-warning-days"
                value={cfg.expiry_warning_days ?? 90}
                onChange={(e) => set({ expiry_warning_days: e.target.value === "" ? null : Number(e.target.value) })} />
            </div>
            <div>
              <Label>{t("ex.criticalDays")}</Label>
              <Input type="number" min="1" className="mt-1.5" disabled={!isAdmin} data-testid="tc-expiry-critical-days"
                value={cfg.expiry_critical_days ?? 30}
                onChange={(e) => set({ expiry_critical_days: e.target.value === "" ? null : Number(e.target.value) })} />
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground" data-testid="tc-expiry-hint">{t("ex.thresholdHint")}</p>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span data-testid="tc-history-note">{t("tc.historyNote")}</span>
        </div>

        {isAdmin && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
            <Button variant="outline" disabled={busy || !cfg.country_code} data-testid="tc-apply-defaults"
              onClick={() => save({ apply_country_defaults: true }, { country_code: cfg.country_code })} title={t("tc.applyHint")}>
              {t("tc.applyDefaults")}
            </Button>
            <Button disabled={busy} onClick={() => save()} data-testid="tc-save">{t("common.save")}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
